import React, { useState } from "react";
import { toast } from "sonner";
import { copyText } from "../../lib/clipboard";
import { Copy, ExternalLink, KeyRound, Link2, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { api, errMsg } from "../../lib/api";

const LinkBox = ({ url, hasPasscode }) => (
  <div className="min-w-0 rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="share-link-panel">
    <div className="flex items-center gap-2 text-sm text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Link is on{hasPasscode && <span className="flex items-center gap-1 text-xs text-amber-600"><KeyRound size={11} /> passcode required</span>}</div>
    <div className="flex items-center gap-2 min-w-0">
      <a href={url} target="_blank" rel="noreferrer" className="flex-1 min-w-0 truncate text-sm font-mono text-slate-900 hover:underline" data-testid="share-link-url">{url}</a>
      <button onClick={() => copyText(url, "Link copied")} data-testid="share-copy-btn" className="text-slate-500 hover:text-slate-900" aria-label="Copy link"><Copy size={14} /></button>
      <a href={url} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-900" aria-label="Open link"><ExternalLink size={14} /></a>
    </div>
    <p className="text-xs text-slate-500">Always shows your latest version — even unpublished changes.</p>
  </div>
);

export const ShareDialog = ({ open, onOpenChange, project, onChange }) => {
  const [usePass, setUsePass] = useState(false);
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const share = project.share;
  const url = share ? `${window.location.origin}/s/${share.token}` : "";

  const act = async (fn, msg) => {
    setBusy(true);
    try { onChange(await fn()); toast.success(msg); setPass(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const create = () => act(() => api.createShare(project.id, usePass ? pass : null), share ? "New link created — the old one stopped working" : "Private link created");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white border-slate-200" data-testid="share-dialog">
        <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2"><Link2 size={18} className="text-emerald-600" /> Share a private preview</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">Only people with the link can open it. It isn't listed anywhere.</DialogDescription>
        {share && <LinkBox url={url} hasPasscode={share.has_passcode} />}
        <div className="space-y-3">
          <label className="flex items-center justify-between gap-3 text-sm text-slate-600">
            <span className="flex items-center gap-2"><KeyRound size={14} className="text-amber-600" /> Require a passcode</span>
            <Switch checked={usePass} onCheckedChange={setUsePass} data-testid="share-passcode-switch" className="data-[state=checked]:bg-emerald-500" />
          </label>
          {usePass && (
            <input value={pass} onChange={(e) => setPass(e.target.value)} placeholder="At least 4 characters" maxLength={64} data-testid="share-passcode-input"
              className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-900 font-mono outline-none focus:border-sky-400" />
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={create} disabled={busy || (usePass && pass.trim().length < 4)} data-testid="share-create-btn"
            className="flex-1 h-11 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : share ? <RefreshCw size={14} /> : <Link2 size={14} />} {share ? "Create a new link" : "Create link"}
          </button>
          {share && (
            <button onClick={() => act(() => api.deleteShare(project.id), "Link turned off")} disabled={busy} data-testid="share-disable-btn"
              className="h-11 px-4 rounded-lg border border-slate-300 text-sm text-slate-600 hover:text-rose-600 hover:border-rose-300">Turn off</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
